import { createRoot } from "react-dom/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { getBrandThemes, brandPrefersDark } from "../theme";
import { getActiveBrand } from "../branding/activeBrand";
import CloseIcon from "@mui/icons-material/Close";
import ReactMarkdown from "react-markdown";

// Store the current dark mode value
let currentDarkMode = false;

// Function to update the dark mode value
export const updateMarkdownDialogDarkMode = (isDark: boolean) => {
  currentDarkMode = isDark;
};

// Initialize with the current dark mode value from localStorage
const savedDarkMode = localStorage.getItem("darkMode.v2");
if (savedDarkMode) {
  currentDarkMode = JSON.parse(savedDarkMode);
}

interface MarkdownDialogProps {
  title: string;
  content: string;
}

function MarkdownDialog({
  title,
  content,
  onClose,
}: MarkdownDialogProps & { onClose: () => void }) {
  const brand = getActiveBrand();
  const themes = getBrandThemes(brand.key);
  // Respect a brand that ships dark (CerebraLinked) even if the toggle preference is unset.
  const dark = currentDarkMode || brandPrefersDark(brand.key);
  const theme = dark ? themes.dark : themes.light;

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open
        maxWidth="md"
        fullWidth
        // onClose handles both Escape and backdrop click — previously the dialog
        // could only be dismissed via the X button (a keyboard trap).
        onClose={onClose}
        aria-labelledby="markdown-dialog-title"
      >
        <DialogTitle id="markdown-dialog-title">
          {title}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: "70vh", overflow: "auto" }}>
            <ReactMarkdown>{content}</ReactMarkdown>
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}

// Create a wrapper function that manages the dialog state
function createMarkdownDialog({ title, content }: MarkdownDialogProps) {
  const div = document.createElement("div");
  document.body.appendChild(div);

  const root = createRoot(div);

  const cleanup = () => {
    // Defer so we never unmount synchronously from within the dialog's own
    // React event handler.
    setTimeout(() => {
      root.unmount();
      div.remove();
    }, 0);
  };

  root.render(
    <MarkdownDialog title={title} content={content} onClose={cleanup} />,
  );

  return cleanup;
}

export default createMarkdownDialog;
